import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { getAllDiagnosisReports, getReportsStatistics } from '../../services/diagnosisReports.service';
import { getServiceTargets } from '../../services/target.service';
import { getPlanningStats } from '../../services/planning.service';
import { getFranchiseAdminsCount } from '../../services/franchise.service';
import '../../styles/admin-dashboard.css';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const SERVICE_COLORS = {
    'Financial Diagnosis': {
        bg: 'rgba(99, 102, 241, 0.7)',
        border: 'rgba(99, 102, 241, 1)'
    },
    'Roadmap': {
        bg: 'rgba(16, 185, 129, 0.7)',
        border: 'rgba(16, 185, 129, 1)'
    },
    'Hands-on': {
        bg: 'rgba(245, 158, 11, 0.7)',
        border: 'rgba(245, 158, 11, 1)'
    },
    'Coaching Training': {
        bg: 'rgba(239, 68, 68, 0.7)',
        border: 'rgba(239, 68, 68, 1)'
    }
};

const Dashboard = () => {
    const [stats, setStats] = useState({
        totalReports: 0,
        totalRevenue: 0,
        averageIncome: 0,
        averageExpenses: 0,
        averageSavingsRate: 0,
        roadmapRevenue: 0,
        roadmapCount: 0,
        totalDemoReports: 0,
        franchiseAdminCount: 0,
        weeklyReports: [0, 0, 0, 0, 0, 0, 0],
        monthlySales: Array(12).fill(0)
    });
    const [recentReports, setRecentReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [chartType, setChartType] = useState('bar'); // 'pie' or 'bar'
    const [targetData, setTargetData] = useState({
        targets: {},
        achieved: {}
    });
    const [weeklyTrend, setWeeklyTrend] = useState({
        labels: [],
        counts: [],
        revenues: []
    });
    const [weekOffset, setWeekOffset] = useState(0); // 0 for current week, 1 for previous, etc.
    const [trendMetric, setTrendMetric] = useState('count'); // 'count' or 'revenue'
    const [trendChartType, setTrendChartType] = useState('bar'); // 'bar' or 'line'
    const [allReportsCache, setAllReportsCache] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [originalPlanningData, setOriginalPlanningData] = useState({});
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    useEffect(() => {
        fetchDashboardData();
    }, []);

    useEffect(() => {
        if (allReportsCache.length > 0) {
            processWeeklyTrend(allReportsCache);
        }
    }, [weekOffset, allReportsCache]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const [statsData, allReports, targets, planningData, franchiseCount] = await Promise.all([
                getReportsStatistics(),
                getAllDiagnosisReports(),
                getServiceTargets(),
                getPlanningStats(),
                getFranchiseAdminsCount()
            ]);

            setRecentReports(allReports.slice(0, 5));

            // USE REAL DATABASE VALUES FOR ACHIEVEMENT
            const achieved = {
                'Financial Diagnosis': allReports.length,
                'Roadmap': planningData.roadmapCount || 0,
                'Hands-on': planningData.handsonCount || 0,
                'Coaching Training': planningData.coachingCount || 0
            };

            setTargetData({ targets, achieved });

            setOriginalPlanningData(planningData);
            setAllReportsCache(allReports);
            updateDashboardView(allReports, { ...statsData, franchiseAdminCount: franchiseCount }, planningData, '', '', '');
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };
    const processWeeklyTrend = (allReports) => {
        const labels = [];
        const counts = Array(7).fill(0);
        const revenues = Array(7).fill(0);
        const now = new Date();
        const shiftDays = weekOffset * 7;

        for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d.setDate(now.getDate() - (i + shiftDays));
            labels.push(d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }));

            const dayReports = allReports.filter(r => {
                const rDate = r.createdAt instanceof Date ? r.createdAt : new Date(r.createdAt);
                if (isNaN(rDate.getTime())) return false;

                return rDate.getDate() === d.getDate() &&
                    rDate.getMonth() === d.getMonth() &&
                    rDate.getFullYear() === d.getFullYear();
            });

            const completedDayReports = dayReports.filter(r => r.status !== 'incomplete');
            counts[6 - i] = completedDayReports.length;
            revenues[6 - i] = completedDayReports.reduce((sum, r) => {
                const isDemo = r.payment?.paymentId?.startsWith('DEMO_');
                return isDemo ? sum : sum + (r.payment?.amount || 299);
            }, 0);
        }

        setWeeklyTrend({ labels, counts, revenues });
    };

    const updateDashboardView = (reports, baseStats, planning, search, dateStart, dateEnd) => {
        let filtered = [...reports];
        const term = search?.toLowerCase().trim();

        // Apply date filter first if set
        if (dateStart || dateEnd) {
            const start = dateStart ? new Date(dateStart) : null;
            const end = dateEnd ? new Date(dateEnd) : null;
            if (end) end.setHours(23, 59, 59, 999); // Include full end date

            filtered = filtered.filter(r => {
                const rDate = r.createdAt instanceof Date ? r.createdAt : new Date(r.createdAt);
                if (start && rDate < start) return false;
                if (end && rDate > end) return false;
                return true;
            });
        }

        if (term) {
            const months = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
            const shortMonths = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

            const monthIdx = months.indexOf(term) !== -1 ? months.indexOf(term) : shortMonths.indexOf(term);

            if (monthIdx !== -1) {
                // Filter by month
                filtered = filtered.filter(r => {
                    const d = new Date(r.createdAt);
                    return d.getMonth() === monthIdx;
                });
            } else {
                // Filter by name, email or city
                filtered = filtered.filter(r =>
                    r.personalInfo?.name?.toLowerCase().includes(term) ||
                    r.personalInfo?.email?.toLowerCase().includes(term) ||
                    r.personalInfo?.city?.toLowerCase().includes(term)
                );
            }
        }

        // Calculate achieved metrics for the filtered set
        const achieved = {
            'Financial Diagnosis': filtered.length,
            'Roadmap': filtered.filter(r => r.planType === 'roadmap').length || 0,
            'Hands-on': filtered.filter(r => r.planType === 'handson').length || 0,
            'Coaching Training': filtered.filter(r => r.planType === 'coaching').length || 0
        };

        setTargetData(prev => ({ ...prev, achieved }));

        // Calculate Stats for Filtered Data
        let demoCount = 0;
        const totalRevenue = filtered.reduce((sum, r) => {
            const isDemo = r.payment?.paymentId?.startsWith('DEMO_');
            if (isDemo) demoCount++;
            if (isDemo || r.status === 'incomplete') return sum;
            return sum + (r.payment?.amount || 299);
        }, 0);

        const completedFiltered = filtered.filter(r => r.status !== 'incomplete');

        setStats({
            ...baseStats,
            totalReports: completedFiltered.length,
            totalRevenue: totalRevenue,
            totalDemoReports: demoCount,
            roadmapCount: achieved['Roadmap'],
            roadmapRevenue: filtered.filter(r => r.planType === 'roadmap').reduce((sum, r) => {
                const isDemo = r.payment?.paymentId?.startsWith('DEMO_');
                if (isDemo || r.status === 'incomplete') return sum;
                return sum + (r.payment?.amount || 0);
            }, 0)
        });

        setRecentReports(filtered.slice(0, 5));
        processWeeklyTrend(filtered);
    };

    const handleSearch = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        updateDashboardView(allReportsCache, stats, originalPlanningData, val, startDate, endDate);
    };

    const handleStartDateChange = (e) => {
        const val = e.target.value;
        setStartDate(val);
        updateDashboardView(allReportsCache, stats, originalPlanningData, searchTerm, val, endDate);
    };

    const handleEndDateChange = (e) => {
        const val = e.target.value;
        setEndDate(val);
        updateDashboardView(allReportsCache, stats, originalPlanningData, searchTerm, startDate, val);
    };

    const clearDateFilter = () => {
        setStartDate('');
        setEndDate('');
        updateDashboardView(allReportsCache, stats, originalPlanningData, searchTerm, '', '');
    };

    const getChartData = () => {
        const labels = Object.keys(targetData.targets);
        const achievedValues = labels.map(l => targetData.achieved[l] || 0);
        const targetValues = labels.map(l => targetData.targets[l] || 0);

        // Map colors consistently to service names
        const backgroundColors = labels.map(l => SERVICE_COLORS[l]?.bg || 'rgba(203, 213, 225, 0.7)');
        const borderColors = labels.map(l => SERVICE_COLORS[l]?.border || 'rgba(203, 213, 225, 1)');

        if (chartType === 'pie') {
            return {
                labels,
                datasets: [
                    {
                        label: 'Achieved',
                        data: achievedValues,
                        backgroundColor: backgroundColors,
                        borderColor: borderColors,
                        borderWidth: 1,
                    },
                ],
            };
        } else {
            return {
                labels,
                datasets: [
                    {
                        label: 'Target',
                        data: targetValues,
                        backgroundColor: 'rgba(203, 213, 225, 0.5)',
                        borderColor: '#94A3B8',
                        borderWidth: 1,
                    },
                    {
                        label: 'Achieved',
                        data: achievedValues,
                        backgroundColor: 'rgba(99, 102, 241, 0.8)',
                        borderColor: '#6366F1',
                        borderWidth: 1,
                    }
                ],
            };
        }
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: { size: 12 }
                }
            }
        },
        scales: chartType === 'bar' ? {
            y: {
                beginAtZero: true,
                grid: { color: '#f1f5f9' }
            },
            x: {
                grid: { display: false }
            }
        } : {}
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(value || 0);
    };

    // Calculate overall target progress (using Financial Diagnosis as primary metric)
    const diagnosisTarget = targetData.targets['Financial Diagnosis'] || 100;
    const progressPercentage = Math.round((stats.totalReports / diagnosisTarget) * 100);

    if (loading) {
        return (
            <div className="admin-content-v2" style={{ display: 'flex', minHeight: '80vh' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', flex: 1 }}>
                    <div className="custom-loader" style={{ width: '55px', height: '80px' }}>
                        <svg viewBox="0 0 55 80" xmlns="http://www.w3.org/2000/svg" fill="#0EAB5EFF">
                            <g transform="matrix(1 0 0 -1 0 80)">
                                <rect width="10" height="20" rx="3">
                                    <animate attributeName="height" begin="0s" dur="4.3s" values="20;45;57;80;64;32;66;45;64;23;66;13;64;56;34;34;2;23;76;79;20" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                                <rect x="15" width="10" height="80" rx="3">
                                    <animate attributeName="height" begin="0s" dur="2s" values="80;55;33;5;75;23;73;33;12;14;60;80" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                                <rect x="30" width="10" height="50" rx="3">
                                    <animate attributeName="height" begin="0s" dur="1.4s" values="50;34;78;23;56;23;34;76;80;54;21;50" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                                <rect x="45" width="10" height="30" rx="3">
                                    <animate attributeName="height" begin="0s" dur="2s" values="30;45;13;80;56;72;45;76;34;23;67;30" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                            </g>
                        </svg>
                    </div>
                    <p style={{ marginTop: '20px', color: '#64748B', fontWeight: 500 }}>Loading dashboard metrics...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-content-v2">
            <div className="dashboard-top-nav-v2">
                <div className="top-nav-left-v2">
                    <h1 className="nav-title-v2">Financial Overview</h1>
                    <p className="nav-subtitle-v2">Welcome back, Admin</p>
                </div>
                <div className="top-nav-right-v2">
                    <div className="filter-container-v2">
                        <div className="search-bar-v2">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                            <input
                                type="text"
                                placeholder="Search Month"
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                        </div>
                        <div className="date-filter-v2">
                            <div className="date-input-group">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                                <input
                                    type="date"
                                    placeholder="From"
                                    value={startDate}
                                    onChange={handleStartDateChange}
                                    className="date-input"
                                />
                            </div>
                            <span className="date-separator">to</span>
                            <div className="date-input-group">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                                <input
                                    type="date"
                                    placeholder="To"
                                    value={endDate}
                                    onChange={handleEndDateChange}
                                    className="date-input"
                                />
                            </div>
                            {(startDate || endDate) && (
                                <button className="clear-date-btn" onClick={clearDateFilter} title="Clear Date Filter">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="stats-grid-v2">
                <div className="stat-card-v2">
                    <span className="stat-label-v2">Diagnostic Revenue</span>
                    <span className="stat-value-v2">{formatCurrency(stats.totalRevenue)}</span>
                    <div className="stat-trend-v2 positive">
                        <span>+{(stats.totalReports > 0 ? 100 : 0)}%</span>
                        <span className="stat-sub-v2">total growth</span>
                    </div>
                    <div className="stat-icon-v2">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>
                    </div>
                </div>

                <div className="stat-card-v2">
                    <span className="stat-label-v2">Total Users</span>
                    <span className="stat-value-v2">{stats.totalReports}</span>
                    <div className="stat-trend-v2 positive">
                        <span>Live</span>
                        <span className="stat-sub-v2">active participants</span>
                    </div>
                    <div className="stat-icon-v2">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                    </div>
                </div>

                <div className="stat-card-v2">
                    <span className="stat-label-v2">Roadmap Revenue</span>
                    <span className="stat-value-v2">{formatCurrency(stats.roadmapRevenue)}</span>
                    <div className="stat-trend-v2 positive">
                        <span>Dynamic</span>
                        <span className="stat-sub-v2">roadmap sales</span>
                    </div>
                    <div className="stat-icon-v2">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>
                    </div>
                </div>

                <div className="stat-card-v2">
                    <span className="stat-label-v2">Roadmap Users</span>
                    <span className="stat-value-v2">{stats.roadmapCount}</span>
                    <div className="stat-trend-v2 positive">
                        <span>Active</span>
                        <span className="stat-sub-v2">planning users</span>
                    </div>
                    <div className="stat-icon-v2">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                    </div>
                </div>

                <div className="stat-card-v2">
                    <span className="stat-label-v2">Demo Diagnoses</span>
                    <span className="stat-value-v2">{stats.totalDemoReports}</span>
                    <div className="stat-trend-v2 positive">
                        <span>Total</span>
                        <span className="stat-sub-v2">demo sessions</span>
                    </div>
                    <div className="stat-icon-v2" style={{ color: '#F59E0B' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" ry="1" /><path d="M9 14l2 2 4-4" /></svg>
                    </div>
                </div>

                <div className="stat-card-v2">
                    <span className="stat-label-v2">Franchise Admins</span>
                    <span className="stat-value-v2">{stats.franchiseAdminCount}</span>
                    <div className="stat-trend-v2 positive">
                        <span>Active</span>
                        <span className="stat-sub-v2">franchise partners</span>
                    </div>
                    <div className="stat-icon-v2" style={{ color: '#10B981' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                    </div>
                </div>
            </div>

            {/* Charts Group */}
            <div className="charts-group-v2">
                <div className="chart-card-v2">
                    <div className="chart-header-v2" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                            <div>
                                <h3>Diagnosis Trends</h3>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <span
                                        onClick={() => setTrendMetric('count')}
                                        style={{ fontSize: '11px', cursor: 'pointer', color: trendMetric === 'count' ? '#10B981' : '#94A3B8', fontWeight: trendMetric === 'count' ? 700 : 400 }}
                                    >
                                        Reports
                                    </span>
                                    <span style={{ color: '#E2E8F0' }}>|</span>
                                    <span
                                        onClick={() => setTrendMetric('revenue')}
                                        style={{ fontSize: '11px', cursor: 'pointer', color: trendMetric === 'revenue' ? '#6366F1' : '#94A3B8', fontWeight: trendMetric === 'revenue' ? 700 : 400 }}
                                    >
                                        Income
                                    </span>
                                </div>
                            </div>
                            <div className="chart-toggle-v2" style={{ marginLeft: 'auto', marginRight: '12px' }}>
                                <button
                                    className={`toggle-btn-v2 ${trendChartType === 'bar' ? 'active' : ''}`}
                                    onClick={() => setTrendChartType('bar')}
                                    style={{ fontSize: '10px', padding: '2px 8px' }}
                                >
                                    Bar
                                </button>
                                <button
                                    className={`toggle-btn-v2 ${trendChartType === 'line' ? 'active' : ''}`}
                                    onClick={() => setTrendChartType('line')}
                                    style={{ fontSize: '10px', padding: '2px 8px' }}
                                >
                                    Line
                                </button>
                            </div>
                            <div className="chart-nav-v2" style={{ display: 'flex', gap: '4px' }}>
                                <button
                                    className="icon-btn-v2"
                                    onClick={() => setWeekOffset(prev => prev + 1)}
                                    style={{ padding: '4px', borderRadius: '4px', border: '1px solid #E2E8F0' }}
                                    title="Previous Week"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                                </button>
                                <button
                                    className="icon-btn-v2"
                                    onClick={() => setWeekOffset(prev => Math.max(0, prev - 1))}
                                    disabled={weekOffset === 0}
                                    style={{ padding: '4px', borderRadius: '4px', border: '1px solid #E2E8F0', opacity: weekOffset === 0 ? 0.3 : 1 }}
                                    title="Next Week"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="chart-container" style={{ height: '180px' }}>
                        {trendChartType === 'bar' ? (
                            <Bar
                                data={{
                                    labels: weeklyTrend.labels,
                                    datasets: [{
                                        label: trendMetric === 'count' ? 'Reports' : 'Revenue (₹)',
                                        data: trendMetric === 'count' ? weeklyTrend.counts : weeklyTrend.revenues,
                                        backgroundColor: trendMetric === 'count' ? '#10B981' : '#6366F1',
                                        borderRadius: 4,
                                        barThickness: 15
                                    }]
                                }}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { display: false },
                                        tooltip: {
                                            mode: 'index',
                                            intersect: false,
                                            callbacks: {
                                                label: function (context) {
                                                    let label = context.dataset.label || '';
                                                    if (label) {
                                                        label += ': ';
                                                    }
                                                    if (context.parsed.y !== null) {
                                                        label += trendMetric === 'revenue' ? `₹${context.parsed.y.toLocaleString()}` : context.parsed.y;
                                                    }
                                                    return label;
                                                }
                                            }
                                        }
                                    },
                                    scales: {
                                        y: {
                                            beginAtZero: true,
                                            grid: { color: 'rgba(226, 232, 240, 0.5)' },
                                            ticks: { display: false }
                                        },
                                        x: {
                                            grid: { display: false },
                                            ticks: { font: { size: 10 }, color: '#64748B' }
                                        }
                                    }
                                }}
                            />
                        ) : (
                            <Line
                                data={{
                                    labels: weeklyTrend.labels,
                                    datasets: [{
                                        label: trendMetric === 'count' ? 'Reports' : 'Revenue (₹)',
                                        data: trendMetric === 'count' ? weeklyTrend.counts : weeklyTrend.revenues,
                                        borderColor: trendMetric === 'count' ? '#10B981' : '#6366F1',
                                        backgroundColor: trendMetric === 'count' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(99, 102, 241, 0.1)',
                                        fill: true,
                                        tension: 0.4,
                                        pointRadius: 4,
                                        pointBackgroundColor: trendMetric === 'count' ? '#10B981' : '#6366F1',
                                        borderWidth: 2
                                    }]
                                }}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { display: false },
                                        tooltip: {
                                            mode: 'index',
                                            intersect: false,
                                            callbacks: {
                                                label: function (context) {
                                                    let label = context.dataset.label || '';
                                                    if (label) {
                                                        label += ': ';
                                                    }
                                                    if (context.parsed.y !== null) {
                                                        label += trendMetric === 'revenue' ? `₹${context.parsed.y.toLocaleString()}` : context.parsed.y;
                                                    }
                                                    return label;
                                                }
                                            }
                                        }
                                    },
                                    scales: {
                                        y: {
                                            beginAtZero: true,
                                            grid: { color: 'rgba(226, 232, 240, 0.5)' },
                                            ticks: { display: false }
                                        },
                                        x: {
                                            grid: { display: false },
                                            ticks: { font: { size: 10 }, color: '#64748B' }
                                        }
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>

                <div className="chart-card-v2" style={{ gridColumn: 'span 1' }}>
                    <div className="chart-header-v2">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <div>
                                <h3>Service Performance</h3>
                                <p className="chart-sub-v2">Target vs Actual</p>
                            </div>
                            <div className="chart-toggle-v2">
                                <button
                                    className={`toggle-btn-v2 ${chartType === 'pie' ? 'active' : ''}`}
                                    onClick={() => setChartType('pie')}
                                >
                                    Pie
                                </button>
                                <button
                                    className={`toggle-btn-v2 ${chartType === 'bar' ? 'active' : ''}`}
                                    onClick={() => setChartType('bar')}
                                >
                                    Graph
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="chart-container" style={{ height: '180px', position: 'relative' }}>
                        {chartType === 'pie' ? (
                            <Pie data={getChartData()} options={chartOptions} />
                        ) : (
                            <Bar data={getChartData()} options={chartOptions} />
                        )}
                    </div>
                </div>

                <div className="chart-card-v2">
                    <div className="chart-header-v2">
                        <h3>Service Target</h3>
                        <p className="chart-sub-v2">Overall progress</p>
                    </div>
                    <div className="chart-content-center-v2">
                        <div className="circular-progress-v2">
                            <svg viewBox="0 0 36 36">
                                <circle
                                    className="progress-bg-v2"
                                    cx="18" cy="18" r="16"
                                />
                                <circle
                                    className="progress-fill-v2"
                                    cx="18" cy="18" r="16"
                                    style={{
                                        strokeDasharray: `${Math.min(progressPercentage, 100)}, 100`,
                                        transition: 'stroke-dasharray 0.5s ease'
                                    }}
                                    stroke="#3B82F6"
                                />
                            </svg>
                            <div className="progress-text-v2">
                                <span className="percentage-v2">{progressPercentage}%</span>
                                <span className="label-v2">Target</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Grid */}
            <div className="data-grid-v2">
                <div className="table-card-v2">
                    <div className="table-card-header-v2">
                        <h3>Diagnosis Submissions</h3>
                    </div>
                    <div className="table-responsive">
                        <table className="v2-table">
                            <thead>
                                <tr>
                                    <th>Participant</th>
                                    <th>Income Type</th>
                                    <th>Paid Amount</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentReports.map(report => (
                                    <tr key={report.id}>
                                        <td>
                                            <div className="user-cell">
                                                <div className="user-avatar-small">
                                                    {report.personalInfo?.name?.charAt(0).toUpperCase() || 'P'}
                                                </div>
                                                <span>{report.personalInfo?.name || 'Unknown'}</span>
                                            </div>
                                        </td>
                                        <td><span className="income-badge-v2">SALARY</span></td>
                                        <td>
                                            {report.status === 'incomplete' ? (
                                                <span style={{ fontWeight: 500, color: '#f97316' }}>incomplete form</span>
                                            ) : (
                                                <span style={{ fontWeight: 600, color: '#059669' }}>
                                                    {formatCurrency(report.payment?.amount || 299)}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="v2-progress-bar">
                                                <div className="v2-progress-fill" style={{ width: `${report.financialHealth?.savingsRate}%` }}></div>
                                            </div>
                                        </td>
                                        <td className="actions-cell">
                                            <div className="action-buttons-v2">
                                                <Link to={`/admin/diagnosis/reports/${report.reportId}`} className="icon-btn-v2 view" title="View">
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                                </Link>
                                                <button className="icon-btn-v2 delete" title="Delete">
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="activity-card-v2">
                    <h3 style={{ marginBottom: '8px' }}>Inbound Activity</h3>
                    <p style={{ fontSize: '12px', color: '#10B981', marginBottom: '24px' }}>24% increase this month</p>
                    <div className="activity-list-v2">
                        {recentReports.map((report, idx) => (
                            <div key={idx} className="activity-item-v2">
                                <div className="activity-icon-v2" style={{ borderColor: idx % 2 === 0 ? '#10B981' : '#EF4444' }}></div>
                                <div className="activity-info-v2">
                                    <h4>Diagnosis by {report.personalInfo?.name || 'Unknown'}</h4>
                                    <span className="activity-time-v2">
                                        {report.createdAt instanceof Date ? report.createdAt.toLocaleDateString() : 'Recent'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
